import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Box,
  IconButton,
  Card,
  CardContent,
  Grid,
} from '@mui/material'
import { Close, Search } from '@mui/icons-material'
import { useState } from 'react'
import { useAppSelector } from '../../hooks/reduxHooks'
import {
  InsiService as ImtiService,
  GetImtiRequest,
  ImtCV,
} from '../../services/ImtiService'
import { PatientReadSourceEnum } from '../../common/interfaces'
import { CouvertureFormRef } from '../../containers/app/billing/form/CouvertureForm'
import { PsModel } from '@/services/InsiService'

type Props = {
  open: boolean
  onClose: () => void
  coverageFormRef: React.RefObject<CouvertureFormRef>
  onDoctorSelect?: (doctor: {
    firstName: string
    lastName: string
    rppsNumber: string
    city: string
    postalCode: string
  }) => void
}

const ImtiSearchResult = ({
  open,
  onClose,
  coverageFormRef,
  onDoctorSelect,
}: Props) => {
  const [isSearching, setIsSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<any>(null)
  const [infoMessage, setInfoMessage] = useState<string | null>(null)

  const { memberSituation } = useAppSelector((state) => ({
    memberSituation: state.order.selectedMemberSituation,
  }))

  const handleSearchImti = async () => {
    if (isSearching) {
      return
    }

    setError(null)
    setInfoMessage(null)
    setResult(null)

    if (!memberSituation) {
      setError(
        'Veuillez sélectionner un médecin disposant d\'une situation valide avant d\'utiliser IMTi',
      )
      return
    }

    const coverageValues = await coverageFormRef.current?.getValues(true)
    if (!coverageValues) {
      setError(
        'Veuillez compléter les informations de couverture ou lire une carte Vitale avant d\'utiliser IMTi',
      )
      return
    }

    const sanitize = (value?: string | number | null) =>
      value !== undefined && value !== null
        ? String(value).replace(/\s+/g, '')
        : ''

    const psModel: PsModel = {
      psNumeroFacturation:
        memberSituation.situation.numIdentificationPSFacturation,
      psIdentStructure: memberSituation.situation.numIdentificationStructure,
      authentificationMode: process.env.REACT_APP_AUTH_MODE || '1',
      codePorteurCPS: process.env.REACT_APP_CODE_PORTEUR_CPS || '0',
    }
    let birthDate = ''
    if (coverageValues.beneficaireDateNaissance) {
      const dateStr = coverageValues.beneficaireDateNaissance
      const dateYear = dateStr.substring(0, 4)
      const dateMonth = dateStr.substring(4, 6)
      const dateDay = dateStr.substring(6, 8)
      birthDate = `${dateYear}-${dateMonth}-${dateDay}`
    }

    const imtCV: ImtCV = {
      assureNumSecu: sanitize(coverageValues.assureNir),
      beneficiaireDateNaissance: birthDate,
      beneficiaireRang: sanitize(coverageValues.rangNaissance) || '1',
      beneficiaireNumSecu: sanitize(coverageValues.beneficiaireNir),
      codeRegime: coverageValues.organismeAmo?.regime || '',
      codeCaisse: coverageValues.organismeAmo?.caisse || '',
      codeCentre: coverageValues.organismeAmo?.centre || '',
      numeroSerieCv: sanitize(coverageValues.numeroSerieCv),
    }

    const imtiPayload: GetImtiRequest = {
      psModel,
      imtCV,
      isApcv: coverageValues.readSource === PatientReadSourceEnum.Apcv,
      idJfse: memberSituation.situation.idJfse,
    }

    try {
      setIsSearching(true)
      const response = await ImtiService.findByTraits(imtiPayload)
      const res = response.message.reponse
      if (res.erreur) {
        const errorMessage = res.erreurMessage || res.erreurMessageComplet
        setError(errorMessage)
        setInfoMessage(null)
      } else {
        const message = res.erreurMessage || res.erreurMessageComplet
        const hasNotFoundMessage = message
          ?.toLowerCase()
          ?.includes('non trouvé')
        if (message) {
          setInfoMessage(message || null)
        }
        if (!hasNotFoundMessage) {
          setResult(response)
        }
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Une erreur est survenue lors de la récupération du médecin traitant'
      setError(message)
      setInfoMessage(null)
    } finally {
      setIsSearching(false)
    }
  }

  const resetForm = () => {
    setResult(null)
    setError(null)
    setInfoMessage(null)
  }

  const handleClose = () => {
    if (isSearching) {
      return
    }
    resetForm()
    onClose()
  }

  const handleSelectDoctor = () => {
    if (result?.message?.imtRetour && onDoctorSelect) {
      const doctor = result.message.imtRetour
      onDoctorSelect({
        firstName: doctor.prenom,
        lastName: doctor.nom,
        rppsNumber: doctor.numeroRpps,
        city: doctor.ville,
        postalCode: doctor.codePostale,
      })
      handleClose()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: { minHeight: '400px' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6">Recherche Médecin Traitant (IMTi)</Typography>
        <IconButton onClick={handleClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ my: 2, display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleSearchImti}
            disabled={isSearching || !memberSituation}
            startIcon={
              isSearching ? <CircularProgress size={20} /> : <Search />
            }
          >
            {isSearching
              ? 'Recherche en cours...'
              : 'Rechercher médecin traitant'}
          </Button>
          <Button variant="outlined" onClick={resetForm} disabled={isSearching}>
            Réinitialiser
          </Button>
        </Box>

        <Box>
          {error && <Alert severity="error">{error}</Alert>}
          {!error && infoMessage && (
            <Alert severity="info">{infoMessage}</Alert>
          )}
          {!isSearching && !error && result && !result.message?.imtRetour && (
            <Alert severity="info">
              Aucun médecin traitant trouvé pour ce bénéficiaire.
            </Alert>
          )}
          {!isSearching && !error && result?.message?.imtRetour && (
            <>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Résultat de la recherche
              </Typography>
              <Card variant="outlined">
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Nom
                      </Typography>
                      <Typography variant="body1">
                        {result.message.imtRetour.nom || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Prénom
                      </Typography>
                      <Typography variant="body1">
                        {result.message.imtRetour.prenom || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Numéro RPPS
                      </Typography>
                      <Typography variant="body1">
                        {result.message.imtRetour.numeroRpps || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Adresse
                      </Typography>
                      <Typography variant="body1">
                        {result.message.imtRetour.adresse || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Ville
                      </Typography>
                      <Typography variant="body1">
                        {result.message.imtRetour.ville || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Code Postal
                      </Typography>
                      <Typography variant="body1">
                        {result.message.imtRetour.codePostale || 'N/A'}
                      </Typography>
                    </Grid>
                    {result.message.imtRetour.numeroPsam && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Numéro PSAM
                        </Typography>
                        <Typography variant="body1">
                          {result.message.imtRetour.numeroPsam}
                        </Typography>
                      </Grid>
                    )}
                    {result.message.imtRetour.numeroAdeli && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Numéro ADELI
                        </Typography>
                        <Typography variant="body1">
                          {result.message.imtRetour.numeroAdeli}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        {result?.message?.imtRetour && (
          <Button
            variant="contained"
            onClick={handleSelectDoctor}
            disabled={isSearching}
          >
            Sélectionner ce médecin
          </Button>
        )}
        <Button variant="outlined" onClick={handleClose} disabled={isSearching}>
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ImtiSearchResult
